<template>
  <v-card
    v-if="viewData"
    flat
  >
    <v-card-title class="py-0 px-0">
      <v-toolbar
        :height="$vuetify.breakpoint.xsOnly ? 44 : 60"
        class="filter-toolbar"
        flat
      >
        <v-btn
          v-if="userCanAdd"
          :color="$t('Restaurant.Options.Toolbar.Button.Color')"
          :icon="$vuetify.breakpoint.xsOnly"
          class="elevation-0 ma-1"
          text
          @click="showOptionAddDialog"
        >
          <v-icon :left="$vuetify.breakpoint.smAndUp">
            {{ $t('Restaurant.Options.Toolbar.Button.Icon') }}
          </v-icon>&nbsp;
          <span v-if="$vuetify.breakpoint.smAndUp">{{ $t('Restaurant.Options.Toolbar.Button.Title') }}</span>
        </v-btn>

        <v-btn
          v-if="posUserHasComponentPermission('KitchenMenu', 'ACCESS')"
          :color="$t('Restaurant.Categories.Toolbar.Button.Products.Color')"
          :icon="$vuetify.breakpoint.xsOnly"
          :to="{name: 'KitchenMenu'}"
          class="elevation-0 ma-1"
          text
        >
          <v-icon
            v-if="$t('Restaurant.Categories.Toolbar.Button.Products.Icon')"
            :left="$vuetify.breakpoint.smAndUp"
          >
            {{ $t('Restaurant.Categories.Toolbar.Button.Products.Icon') }}
          </v-icon>&nbsp;
          <span v-if="$vuetify.breakpoint.smAndUp">{{ $t('Restaurant.Categories.Toolbar.Button.Products.Title').toLocaleUpperCase($i18n.locale) }}</span>
        </v-btn>

        <!--
        <v-btn
          v-if="selectedLanguages.length > 1 && userIsSuperExtraAdmin"
          :disabled="dataTable.loading"
          :loading="dataTable.loading"
          :loading-text="dataTable.text.loadingText"
          :no-data-text="dataTable.text.noDataText"
          :no-results-text="dataTable.text.noResultsText"
          class="purple white--text elevation-0 ma-1"
          @click="translateAllItems()"
        >
          {{ $t('Common.Button.Translate').toLocaleUpperCase($i18n.locale) }}
        </v-btn>
        -->

        <v-spacer />

        <filter-search v-model="dataTable.search.terms" />

        <filter-options
          v-model="filtersModel"
          :filters="filterData"
        />
      </v-toolbar>
    </v-card-title>

    <v-container
      class="ma-0 pa-0"
      fluid
    >
      <v-row>
        <v-col cols="12">
          <v-data-table
            :footer-props="dataTable.footerProps"
            :headers="dataTableHeaders"
            :height="dataTable.resize.bodyHeight"
            :items="dataTable.data"
            :loading="dataTable.loading"
            :loading-text="dataTable.text.loadingText"
            :no-data-text="dataTable.text.noDataText"
            :no-results-text="dataTable.text.noResultsText"
            :options.sync="dataTable.options"
            :search="dataTable.search.terms"
            :server-items-length="dataTable.total"
            fixed-header
            item-key="Id"
          >
            <template #[`item.option_id`]="{item}">
              {{ item.Id }}
            </template>

            <template #[`item.option_name`]="{item}">
              <template v-if="userCanManage">
                <a
                  href="#"
                  @click.prevent="showOptionEditDialog(item)"
                >
                  {{ getTranslatedField(item, 'Name') }}
                </a>

                <div
                  v-if="item.FriendlyName"
                  class="grey--text text-caption"
                >
                  {{ item.FriendlyName }}
                </div>
              </template>
              <template v-else>
                {{ getTranslatedField(item, 'Name') }}
              </template>
            </template>

            <template #[`item.catalogs`]="{item}">
              <template v-for="(catalogId, index) in item.Catalogs">
                <v-chip
                  :key="`catalog-${catalogId}-${index}`"
                  :style="'background-color:#' + (getCatalogById(catalogId).Color ? getCatalogById(catalogId).Color : 'e0e0e0') + '; color: ' + (invertColor(getCatalogById(catalogId).Color ? getCatalogById(catalogId).Color : 'e0e0e0', true)) + ';'"
                  class="mr-1 my-1 px-2"
                  label
                  x-small
                  @click="onCatalogChipClick(catalogId)"
                >
                  {{ getCatalogById(catalogId).Name }}
                </v-chip>
              </template>
            </template>

            <template #[`item.display_type`]="{item}">
              <option-type
                :item="item"
                :types="viewData.types"
              />
            </template>

            <template #[`item.languages`]="{item}">
              <template v-for="lang in selectedLanguages">
                <div
                  :key="lang.Code"
                  class="mx-1 d-inline-block"
                >
                  <v-badge
                    :value="hasMissingLanguages(item, lang)"
                    color="orange"
                    overlap
                    right
                  >
                    <template #badge>
                      <v-icon
                        dark
                        small
                      >
                        priority_high
                      </v-icon>
                    </template>

                    <v-avatar
                      v-if="lang.ImageUrl"
                      size="20"
                    >
                      <img
                        :class="[{'grayscale opacity': hasMissingLanguages(item, lang)}]"
                        :src="lang.ImageUrl"
                      >
                    </v-avatar>

                    <div v-else>
                      {{ $te(`Common.Language.${lang.Code}`) ? $t(`Common.Language.${lang.Code}`).name.toLocaleUpperCase($i18n.locale) : lang.Name.toLocaleUpperCase($i18n.locale) }}
                    </div>
                  </v-badge>
                </div>
              </template>
            </template>

            <template #[`item.priority`]="{item}">
              {{ item.Priority }}
            </template>

            <template #[`item.actions`]="{item}">
              <v-btn
                v-if="userCanAdd"
                :loading="item.DuplicateIsLoading"
                :small="$vuetify.breakpoint.xsOnly"
                class="orange elevation-2 ma-0 mr-1"
                dark
                icon
                @click="duplicateOption(item)"
              >
                <v-icon :small="$vuetify.breakpoint.xsOnly">
                  difference
                </v-icon>
              </v-btn>

              <v-btn
                v-if="userCanManage"
                :small="$vuetify.breakpoint.xsOnly"
                class="cyan elevation-2 ma-0"
                dark
                icon
                @click="showOptionEditDialog(item)"
              >
                <v-icon :small="$vuetify.breakpoint.xsOnly">
                  {{ isFromExternalApi(item) ? 'remove_red_eye' : 'edit' }}
                </v-icon>
              </v-btn>

              <v-btn
                v-if="userCanDelete && !isFromExternalApi(item)"
                :small="$vuetify.breakpoint.xsOnly"
                class="red elevation-2 ma-1"
                dark
                icon
                @click="deleteMenuOption(item)"
              >
                <v-icon :small="$vuetify.breakpoint.xsOnly">
                  delete
                </v-icon>
              </v-btn>
            </template>
          </v-data-table>
        </v-col>
      </v-row>
    </v-container>

    <option-add-dialog
      :catalogs="catalogs"
      :option-to-edit="selectedItem"
      :option-types="viewData.types"
      :readonly="isFromExternalApi(selectedItem)"
      :visible.sync="addEditOptionDialog"
    />

    <confirm-dialog
      :html-content="$t('Restaurant.Options.Dialog.Delete.Content')"
      :html-title="$t('Restaurant.Options.Dialog.Delete.Title')"
      :info-bar="true"
      :info-bar-body="$t('Restaurant.Options.Dialog.Delete.Notification')"
      :visible.sync="confirmDialogDeleteVisible"
      no-button-class="v-btn--outline green--text"
      title-class="red white--text lighten-0"
      yes-button-class="red white--text"
      yes-button-event="on-confirm-delete"
      @on-confirm-delete="onConfirmDelete"
    />

    <confirm-dialog
      :cancel-button="$vuetify.breakpoint.smAndUp"
      :html-content="$t('Restaurant.Options.Dialog.Duplicate.Content')"
      :html-title="$t('Restaurant.Options.Dialog.Duplicate.Title')"
      :visible.sync="confirmDialogDuplicateVisible"
      :width="565"
      no-button-class="v-btn--outline green--text"
      title-class="orange white--text lighten-0"
      yes-button-class="orange white--text"
      yes-button-event="on-confirm-duplicate"
      @on-confirm-duplicate="onConfirmDuplicate"
    />
  </v-card>
</template>

<script>
import AppData from '@/mixins/appdata'
import Auth from '@/mixins/auth'
import Translatable from '@/mixins/translatable'
import DataTable from '@/mixins/data-table/dataTable'
import ConfirmDialog from '@/components/common/ConfirmDialog'
import FilterSearch from '@/components/common/filter/FilterSearch.vue'
import OptionAddDialog from './OptionAddDialog.vue'
import {clone, invertColor} from '@/lib/helper/helper.js'
import FilterOptions from '@/components/common/filter/FilterOptions.vue'
import FilterCommon from '@/mixins/filter/filterCommon.js'
import OptionType from '@/components/restaurant/kitchen/OptionType.vue'

export default {
  components: {
    OptionType,
    FilterOptions,
    FilterSearch,
    'option-add-dialog': OptionAddDialog,
    'confirm-dialog'   : ConfirmDialog
  },
  directives: {},
  mixins    : [AppData, Auth, Translatable, DataTable, FilterCommon],
  data() {
    return {
      confirmDialogDuplicateVisible: false,
      confirmDialogDeleteVisible   : false,
      selectedItem                 : null,
      catalogs                     : [],
      addEditOptionDialog          : false,
      filtersModel                 : {
        lang         : 'Restaurant.Options',
        filterCatalog: {
          key  : 'filter_catalog',
          value: [],
          items: []
        },
        filterAllCatalogs: {
          key  : 'filter_all_catalogs',
          value: [],
          items: []
        }
      },
      dataTable: {
        requestTableConfig: false,
        socketCommand     : {
          list  : window.SocketCommand.Menu.Options.All,
          save  : window.SocketCommand.Menu.Options.Save,
          delete: window.SocketCommand.Menu.Options.Delete
        },
        options: {
          defaultSortBy: 'option_id',
          sortBy       : ['option_id'],
          sortDesc     : [true]
        },
        rejectHeaders: {
          xsOnly   : [],
          smAndDown: [],
          mdAndDown: [],
          lgAndDown: [],
          xlAndDown: []
        },
        headers: [
          {
            text     : this.$t('Restaurant.Options.Table.Headers.Id'),
            value    : 'option_id',
            align    : 'right',
            sortable : true,
            cellClass: 'text-caption'
          },
          {
            text     : this.$t('Restaurant.Options.Table.Headers.Name'),
            value    : 'option_name',
            align    : 'left',
            sortable : true,
            cellClass: 'text-caption'
          },
          {
            text     : this.$t('Restaurant.Options.Table.Headers.Catalog'),
            value    : 'catalogs',
            align    : 'left',
            sortable : true,
            cellClass: 'text-caption'
          },
          {
            text     : this.$t('Restaurant.Options.Table.Headers.DisplayType'),
            value    : 'display_type',
            align    : 'left',
            sortable : true,
            cellClass: 'text-caption'
          },
          {
            text     : this.$t('Restaurant.Options.Table.Headers.Languages'),
            value    : 'languages',
            align    : 'center',
            sortable : false,
            cellClass: 'text-caption'
          },
          {
            text     : this.$t('Restaurant.Options.Table.Headers.Priority'),
            value    : 'priority',
            align    : 'center',
            sortable : true,
            cellClass: 'text-caption'
          },
          {
            text     : this.$t('Restaurant.Options.Table.Headers.Actions'),
            value    : 'actions',
            align    : 'right',
            sortable : false,
            cellClass: 'text-caption px-2',
            width    : 140
          }
        ]
      }
    }
  },
  computed: {
    catalogsAllIds() {
      return this.catalogsFilterData.map(catalog => catalog.Id)
    },

    catalogsInternalIds() {
      return this.catalogsFilterData.filter(catalog => !catalog.Api && catalog.Status).map(catalog => catalog.Id)
    },

    catalogsFilterData() {
      const catalogs = clone(this.catalogs)
      catalogs.unshift({
        Id         : -1,
        Name       : this.$t('Restaurant.Options.Filter.NoCatalog.Title'),
        Description: this.$t('Restaurant.Options.Filter.NoCatalog.Subtitle'),
        Color      : 'f48a00',
        Default    : false,
        Status     : true
      })

      return catalogs.map(o => {
        o.Avatar = {Color: `#${o.Color}`}
        return o
      })
    }
  },
  watch: {},
  created() {
    // console.log('1. created');

    // Init Custom Filter Values
    this.catalogs = this.appConfig?.LOCATION_DATA?.Catalogs || []
    this.filtersModel.filterCatalog.items = this.catalogsFilterData
    this.filtersModel.filterCatalog.value = this.catalogsInternalIds
    this.filtersModel.filterAllCatalogs.value = this.catalogsAllIds
  },
  mounted() {
    // console.log('2. mounted');
    this.$bus.$on(window.SocketCommand.Menu.Options.Duplicate, this.onDuplicateOptionResult)
  },
  updated() {
    // console.log('3. updated');
  },
  beforeDestroy() {
    // console.log('4. destroyed');
    this.$bus.$off(window.SocketCommand.Menu.Options.Duplicate, this.onDuplicateOptionResult)
  },
  methods: {
    invertColor,

    isFromExternalApi(product) {
      return !!product?.Api
    },

    duplicateOption(option) {
      this.selectedItem = option
      this.confirmDialogDuplicateVisible = true
    },

    onConfirmDuplicate() {
      this.$set(this.selectedItem, 'DuplicateIsLoading', true)
      window.callAS(window.SocketCommand.Menu.Options.Duplicate, {Id: this.selectedItem.Id})
    },

    onDuplicateOptionResult(data) {
      const viewDataItems = this.viewData?.items || this.viewData?.table?.items?.items
      const item = viewDataItems?.find(option => parseInt(option?.Id) === parseInt(data?.result?.old))
      if (item) this.$set(item, 'DuplicateIsLoading', false)

      if (data.status === 'success') {
        this.getData()

        this.$bus.$emit('app-show-notification', {
          body: this.$t('Restaurant.Options.Dialog.Duplicate.Notification', {id: data?.result?.new || ''}),
          type: 'success',
          icon: 'check'
        })
      } else {
        this.$bus.$emit('app-show-notification', {
          body: this.$t('Common.Misc.Notification.GenericError'),
          type: 'error',
          icon: 'warning'
        })
      }
    },

    getCatalogById(catalogId) {
      return this.catalogs?.find(c => c.Id === catalogId) || ''
    },

    onCatalogChipClick(catalogId) {
      if (!this.filtersModel.filterCatalog.value.includes(catalogId)) {
        this.filtersModel.filterCatalog.value.push(catalogId)
      }
    },

    setData(data) {
      this.$set(this.viewData, 'types', data.types || [])

      if (data?.items) {
        for (let i = 0; i < data.items.length; i++) {
          for (let ii = 0; ii < data.items[i].OptionValues.length; ii++) {
            if (data.items[i].DisplayType === 'unit') {
              data.items[i].OptionValues[ii].Price = parseInt(data.items[i].OptionValues[ii].Price)
            } else {
              data.items[i].OptionValues[ii].Price = parseFloat(data.items[i].OptionValues[ii].Price).toFixed(2)
            }
          }
        }
      }

      this.setDataInternal(data)
    },

    hasMissingLanguages(item, lang) {
      const retVal = !item.Lang[lang.Code] || !item.Lang[lang.Code].Name

      if (Array.isArray(item.OptionValues)) {
        for (let i = 0; i < item.OptionValues.length; i++) {
          const optionValue = item.OptionValues[i]
          if (!optionValue.Lang[lang.Code] || !optionValue.Lang[lang.Code].Name) {
            return true
          }
        }
      }

      return retVal
    },

    showOptionAddDialog() {
      this.selectedItem = null
      this.addEditOptionDialog = true
    },

    showOptionEditDialog(menuOption) {
      this.selectedItem = menuOption
      this.addEditOptionDialog = true
    },

    deleteMenuOption(menuOption) {
      this.selectedItem = menuOption
      this.confirmDialogDeleteVisible = true
    },

    onConfirmDelete() {
      window.callAS(window.SocketCommand.Menu.Options.Delete, {menu_option_id: this.selectedItem.Id})
    },

    onDeleteResult(data) {
      this.onDeleteResultInternal(data)

      if (data.status === 'success') {
        this.addEditCategoryDialog = false
        this.$bus.$emit('app-show-notification', {
          body: data.type,
          type: 'success',
          icon: 'check'
        })
      } else {
        this.$bus.$emit('app-show-notification', {
          body: this.$t('Restaurant.Options.Notification.Update'),
          type: 'error',
          icon: 'warning'
        })
      }
    }

    /*
    async translateAllItems(localeFrom = this.defaultLanguage.Code, localeTo) {
      if (this.selectedLanguages.length <= 0 || !this.userIsSuperExtraAdmin) return
      this.dataTable.loading = true
      const langs = localeTo ? [{Code: localeTo}] : this.selectedLanguages.filter(lang => lang.Code !== localeFrom) || []

      for (let lng = 0; lng < langs.length; lng++) {
        localeTo = langs[lng].Code

        const payload = APICall.CreatePayload(window.SocketCommand.Menu.Options.All, {
          config       : 0,
          page         : 1,
          page_limit   : -1,
          sort_by      : 'option_id',
          order_by     : 'DESC',
          filter_search: ''
        })

        const items_result = await API.post(APICall.api, payload)

        const lngArrName = []
        for (let i = 0; i < items_result.data.length; i++) {
          const item = items_result.data[i]
          lngArrName.push(item.Lang[localeFrom].Name || '')
        }

        const resultName = await googleTranslate(lngArrName, localeTo, localeFrom)

        if (resultName) {
          for (let i = 0; i < items_result.data.length; i++) {
            const item = items_result.data[i]
            const langObj = {}
            for (const lang of this.selectedLanguages) {
              langObj[lang.Code] = {
                Name  : '',
                Locale: lang.Code
              }
            }
            item.Lang = Object.assign({}, langObj, item.Lang)
            item.OptionValues.map(optionValue => optionValue.Lang = Object.assign({}, langObj, optionValue.Lang))
            item.OptionValues = JSON.parse(JSON.stringify(item.OptionValues))
            item.Lang[localeTo].Name = Array.isArray(resultName) ? resultName[i].translatedText : resultName.translatedText

            for (let lng = 0; lng < langs.length; lng++) {
              localeTo = langs[lng].Code
              const trs = []
              for (let opt = 0; opt < item.OptionValues.length; opt++) {
                trs.push(item.OptionValues[opt].Lang[localeFrom].Name || '')
              }

              const result = await googleTranslate(trs, localeTo, localeFrom)

              if (result) {
                for (let opt = 0; opt < item.OptionValues.length; opt++) {
                  item.OptionValues[opt].Lang[localeTo].Name = Array.isArray(result) ? result[opt].translatedText : result.translatedText
                }
              }
            }

            await API.post(APICall.api, APICall.CreatePayload(window.SocketCommand.Menu.Options.Save, {option: item}))
          }
        }
      }
      this.getData()
      this.dataTable.loading = false
    }
     */
  }
}
</script>

<style scoped>
:deep(.filter-toolbar .v-toolbar__content) {
  padding: 0 6px;
}
</style>
